<template>
  <div
    v-if="modelValue"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="$emit('update:modelValue', false)"
      ></div>

      <!-- Modal panel -->
      <div
        class="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
      >
        <form @submit.prevent="handleSubmit">
          <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 bg-white dark:bg-gray-800">
            <div class="sm:flex sm:items-start">
              <div class="w-full">
                <h3 class="text-lg leading-6 font-medium mb-4 text-gray-900 dark:text-gray-100">
                  {{ editMode ? 'Edit Worker Filter' : 'Create Worker Filter' }}
                </h3>

                <!-- Developer Topic Input -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Developer Topic *
                    <span
                      v-if="formData.isIntegrations"
                      class="text-xs text-blue-600 dark:text-blue-400 ml-1 font-normal"
                    >
                      (Auto-populated)
                    </span>
                    <span class="relative inline-block ml-1 group">
                      <svg
                        class="w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help inline-block"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div
                        class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10 pointer-events-none"
                      >
                        Ensure this topic exists
                        <div
                          class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"
                        ></div>
                      </div>
                    </span>
                  </label>
                  <input
                    v-model="formData.developerTopic"
                    type="text"
                    :disabled="formData.isIntegrations"
                    :class="[
                      'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1',
                      formData.isIntegrations
                        ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-500'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
                    ]"
                    placeholder="e.g., my-developer-topic"
                    required
                  />
                </div>

                <!-- Added By Input -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Added By *</label
                  >
                  <input
                    v-model="formData.addedBy"
                    type="text"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    placeholder="e.g., John Doe"
                    required
                  />
                </div>

                <!-- Subscription Input -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Subscription</label
                  >
                  <input
                    v-model="formData.subscription"
                    type="text"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    placeholder="e.g., my-subscription"
                  />
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional subscription identifier for this filter
                  </p>
                </div>

                <!-- Is Integrations Checkbox -->
                <div class="mb-4">
                  <div class="flex items-center">
                    <input
                      id="isIntegrations"
                      v-model="formData.isIntegrations"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                    />
                    <label
                      for="isIntegrations"
                      class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                    >
                      IsInterop Filter
                    </label>
                  </div>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Check this if this filter is specifically for interop
                  </p>
                </div>

                <!-- Match Condition -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Match Condition *</label
                  >
                  <div class="relative">
                    <textarea
                      v-model="formData.matchCondition"
                      class="w-full h-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                      placeholder='{ "key": "value" }'
                      required
                    ></textarea>
                  </div>
                  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Enter a JSON object that defines the match condition for filtering events.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse bg-gray-50 dark:bg-gray-700">
            <button
              type="submit"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-blue-500 text-base font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 sm:w-auto sm:text-sm"
              :disabled="isSubmitting"
            >
              {{ editMode ? 'Update' : 'Create' }} Filter
            </button>
            <button
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
              @click="$emit('update:modelValue', false)"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, defineProps, defineEmits } from 'vue'
import { packagesApiClient } from '../../../services/api/packagesApiClient'
import type { WorkerFilter } from '../../../types/workers-filter'

const props = defineProps<{
  modelValue: boolean
  editMode?: boolean
  filterData?: WorkerFilter | null
  subscription?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'created'): void
  (e: 'updated', filter: WorkerFilter): void
}>()

const isSubmitting = ref(false)

const formData = reactive({
  developerTopic: '',
  addedBy: '',
  matchCondition: '',
  isIntegrations: false,
  subscription: props.subscription || ''
})

// Initialize form data when filterData changes
watch(
  () => props.filterData,
  (newFilter) => {
    if (newFilter && props.editMode) {
      formData.developerTopic = newFilter.developerTopic
      formData.addedBy = newFilter.addedBy || ''
      formData.matchCondition =
        typeof newFilter.matchCondition === 'string'
          ? newFilter.matchCondition
          : JSON.stringify(newFilter.matchCondition, null, 2)
      formData.isIntegrations = newFilter.isIntegrations || false
      formData.subscription = newFilter.subscription || ''
    } else {
      formData.developerTopic = ''
      formData.addedBy = ''
      formData.matchCondition = ''
      formData.isIntegrations = false
      formData.subscription = props.subscription || ''
    }
  },
  { immediate: true }
)

// Auto-populate developer topic when subscription ends with "-sub" AND isIntegrations is checked
const updateDeveloperTopic = () => {
  if (formData.subscription && formData.subscription.endsWith('-sub') && formData.isIntegrations) {
    const baseName = formData.subscription.slice(0, -4) // Remove "-sub"
    formData.developerTopic = `${baseName}-itr-tpc`
  } else if (!formData.isIntegrations) {
    // Clear auto-populated value when checkbox is unchecked to allow manual entry
    if (formData.developerTopic.endsWith('-itr-tpc')) {
      formData.developerTopic = ''
    }
  }
}

// Watch both subscription and isIntegrations checkbox
watch(() => formData.subscription, updateDeveloperTopic)
watch(() => formData.isIntegrations, updateDeveloperTopic)

const handleSubmit = async () => {
  try {
    isSubmitting.value = true

    // Validate JSON
    let matchCondition: any
    try {
      matchCondition = JSON.parse(formData.matchCondition)
    } catch (e) {
      alert('Invalid JSON in match condition')
      return
    }

    if (props.editMode && props.filterData) {
      // Emit update event with updated filter data
      emit('updated', {
        ...props.filterData,
        developerTopic: formData.developerTopic,
        addedBy: formData.addedBy,
        matchCondition,
        isIntegrations: formData.isIntegrations,
        subscription: formData.subscription
      })
    } else {
      // Create new filter
      await packagesApiClient.post('/platform/workers/version-configs', {
        subscription: formData.subscription,
        developerTopic: formData.developerTopic,
        addedBy: formData.addedBy,
        matchCondition,
        isIntegrations: formData.isIntegrations
      })
      emit('created')
    }

    emit('update:modelValue', false)
  } catch (error) {
    // Failed to process filter - use alert for user feedback instead of console.error
    alert('Failed to process filter. Please check your input and try again.')
  } finally {
    isSubmitting.value = false
  }
}
</script>
